/**
 * Rudrik Fraud Network Analysis
 * =============================
 * Graph-based fraud-ring detection. Paste a JSON array of accounts (or load a
 * sample scenario) and see the connection graph, fraud-ring patterns, high
 * risk nodes, and recommended actions.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Network, Loader2, AlertTriangle, FlaskConical, Users, ShieldAlert,
  GitBranch, FileWarning, Sparkles, Workflow,
} from "lucide-react";
import { toast } from "sonner";

const API = `${window.location.origin}/api/fraud-network`;

const RISK = { LOW: "#22c55e", MEDIUM: "#f59e0b", HIGH: "#f97316", CRITICAL: "#dc2626" };
const STRENGTH = { STRONG: "#dc2626", MEDIUM: "#f97316", WEAK: "#f59e0b" };

const NODE_TYPE_COLOR = {
  ACCOUNT: "#60a5fa",
  CUSTOMER: "#60a5fa",
  DEVICE: "#a855f7",
  IP: "#22d3ee",
  BENEFICIARY: "#f97316",
};

function NetworkGraph({ nodes, connections, width = 600, height = 360 }) {
  // simple radial / force-free layout: anchor ACCOUNT nodes around a circle,
  // attribute nodes (DEVICE/IP/etc.) in the centre area.
  const positions = useMemo(() => {
    const accountNodes = nodes.filter((n) => n.type === "ACCOUNT" || n.type === "CUSTOMER");
    const otherNodes = nodes.filter((n) => n.type !== "ACCOUNT" && n.type !== "CUSTOMER");
    const cx = width / 2;
    const cy = height / 2;
    const radius = Math.min(width, height) * 0.36;
    const pos = {};
    accountNodes.forEach((n, i) => {
      const angle = (2 * Math.PI * i) / Math.max(1, accountNodes.length) - Math.PI / 2;
      pos[n.id] = { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
    });
    const inner = radius * 0.42;
    otherNodes.forEach((n, i) => {
      const angle = (2 * Math.PI * i) / Math.max(1, otherNodes.length);
      pos[n.id] = { x: cx + inner * Math.cos(angle), y: cy + inner * Math.sin(angle) };
    });
    return pos;
  }, [nodes, width, height]);

  if (!nodes?.length) {
    return (
      <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", fontSize: 12 }}>
        No graph data
      </div>
    );
  }

  return (
    <svg width={width} height={height} data-testid="network-graph"
         style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 10 }}>
      {/* edges */}
      {connections.map((c, i) => {
        const a = positions[c.from], b = positions[c.to];
        if (!a || !b) return null;
        return (
          <line key={`${c.from}|${c.to}|${c.connection_type}|${i}`}
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke={STRENGTH[c.strength] || "#475569"}
                strokeOpacity={0.55}
                strokeWidth={c.strength === "STRONG" ? 2.2 : c.strength === "MEDIUM" ? 1.5 : 1}
                strokeDasharray={c.strength === "WEAK" ? "4 3" : "none"} />
        );
      })}
      {/* nodes */}
      {nodes.map((n) => {
        const p = positions[n.id];
        if (!p) return null;
        const isAcct = n.type === "ACCOUNT" || n.type === "CUSTOMER";
        const color = NODE_TYPE_COLOR[n.type] || "#94a3b8";
        const ring = RISK[n.risk_level] || "#475569";
        const r = isAcct ? 18 : 12;
        return (
          <g key={n.id}>
            <circle cx={p.x} cy={p.y} r={r + 3} fill="none" stroke={ring} strokeWidth={2} opacity={0.85} />
            <circle cx={p.x} cy={p.y} r={r} fill={color} fillOpacity={0.22} stroke={color} strokeWidth={1.5} />
            <text x={p.x} y={p.y + 4} textAnchor="middle" fontSize={isAcct ? 10 : 9} fontWeight={700} fill="#f1f5f9">
              {(n.name || n.id).slice(0, isAcct ? 10 : 8)}
            </text>
            <text x={p.x} y={p.y + r + 14} textAnchor="middle" fontSize={8} fill="#94a3b8"
                  letterSpacing={0.6}>
              {n.type}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

const DEFAULT_PAYLOAD = `{
  "anchor_id": "ACC-001",
  "accounts": [
    { "id": "ACC-001", "name": "Rahul Sharma", "device_id": "DEV-MULE-A", "ip_address": "103.21.58.4", "phone": "+91-9876500001", "beneficiaries": ["BEN-44"], "sources": ["SRC-EXCH"], "total_inflow_inr": 480000, "total_outflow_inr": 475000, "created_days_ago": 12 },
    { "id": "ACC-002", "name": "Priya Patel",  "device_id": "DEV-MULE-A", "ip_address": "103.21.58.4", "phone": "+91-9876500002", "beneficiaries": ["BEN-44"], "sources": ["SRC-EXCH"], "total_inflow_inr": 460000, "total_outflow_inr": 455000, "created_days_ago": 14 },
    { "id": "ACC-003", "name": "Amit Kumar",   "device_id": "DEV-MULE-A", "ip_address": "103.21.58.4", "phone": "+91-9876500003", "beneficiaries": ["BEN-44"], "sources": ["SRC-EXCH"], "total_inflow_inr": 470000, "total_outflow_inr": 465000, "created_days_ago": 9 }
  ]
}`;

export default function FraudNetworkPage() {
  const [payloadText, setPayloadText] = useState(DEFAULT_PAYLOAD);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(640);

  useEffect(() => {
    axios.get(`${API}/sample-scenarios`, { withCredentials: true })
      .then(({ data }) => setScenarios(data.scenarios || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!wrapRef.current) return;
    const observer = new ResizeObserver(() => {
      if (wrapRef.current) setWidth(Math.max(420, wrapRef.current.clientWidth - 28));
    });
    observer.observe(wrapRef.current);
    return () => observer.disconnect();
  }, []);

  const applyScenario = (s) => {
    setPayloadText(JSON.stringify(s.payload, null, 2));
    setResult(null);
    toast.success(`Loaded: ${s.label}`);
  };

  const analyze = async () => {
    let parsed;
    try {
      parsed = JSON.parse(payloadText);
    } catch (err) {
      toast.error("Invalid JSON payload");
      return;
    }
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/analyze`, parsed, { withCredentials: true, timeout: 25000 });
      setResult(data.result);
      const r = data.result;
      const msg = `${r.network_risk} · ${r.network_size}-account network`;
      if (r.network_risk === "CRITICAL") toast.error(msg);
      else if (r.network_risk === "HIGH") toast.warning(msg);
      else if (r.network_risk === "MEDIUM") toast.warning(msg);
      else toast.success(msg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Network analysis failed");
    } finally { setLoading(false); }
  };

  const riskColor = RISK[result?.network_risk] || "#94a3b8";

  return (
    <div data-testid="fraud-network-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center gap-3 mb-1">
        <Network style={{ width: 26, height: 26, color: "#a855f7" }} />
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Fraud Network Analysis</h1>
        <span style={{ background: "rgba(168,85,247,0.12)", color: "#c4b5fd", border: "1px solid #a855f755", fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Session-only
        </span>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 16 }}>
        Graph-based fraud-ring detection. Surfaces money-mule rings, structuring rings, hawala networks,
        synthetic identity farms, and account farms by analysing shared device / IP / phone / address /
        beneficiary attributes.
      </p>

      {scenarios.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
          <span style={{ color: "#64748b", fontSize: 11, alignSelf: "center", marginRight: 6, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <FlaskConical size={11} /> Quick demos:
          </span>
          {scenarios.map((s) => (
            <button key={s.id} onClick={() => applyScenario(s)} data-testid={`sample-${s.id}`}
              style={{ background: "#0d1117", border: "1px solid #1e2530", color: "#cbd5e1", borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <section style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14 }}>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <Users size={11} style={{ color: "#a855f7" }} /> Accounts JSON
          </div>
          <textarea value={payloadText} onChange={(e) => setPayloadText(e.target.value)} data-testid="payload-input"
            style={{ width: "100%", height: 240, background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: 10, color: "#cbd5e1", fontFamily: "ui-monospace, monospace", fontSize: 11, resize: "vertical" }} />
          <button onClick={analyze} disabled={loading} data-testid="analyze-btn"
            style={{ marginTop: 10, width: "100%", background: loading ? "#1e2530" : "linear-gradient(135deg, #a855f7, #7c3aed)",
                     border: "none", color: "white", borderRadius: 6, padding: "10px 18px",
                     fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
                     display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {loading ? <Loader2 className="animate-spin" size={14} /> : <Workflow size={14} />}
            {loading ? "Analysing network…" : "Analyse fraud network"}
          </button>
        </section>

        <section ref={wrapRef} style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14 }}>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <GitBranch size={11} style={{ color: "#a855f7" }} /> Connection Graph
          </div>
          <NetworkGraph nodes={result?.nodes || []} connections={result?.connections || []}
                        width={width} height={300} />
          {!result && (
            <div style={{ marginTop: 8, color: "#64748b", fontSize: 11, textAlign: "center" }}>
              Run an analysis to see the graph
            </div>
          )}
        </section>
      </div>

      {result && (
        <>
          <div data-testid="network-summary" style={{
            background: "#0d1117", border: `1px solid ${riskColor}66`, borderRadius: 12, padding: 22, marginBottom: 12,
            display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr 1fr", gap: 16, alignItems: "center",
          }}>
            <div>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Network</div>
              <div data-testid="network-id" style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 700, marginTop: 4 }}>
                {result.network_id}
              </div>
              <div style={{ color: "#cbd5e1", fontSize: 12, marginTop: 6 }}>{result.summary}</div>
              <div style={{ color: "#64748b", fontSize: 10, marginTop: 6 }}>
                {result.ai_mode} · {result.latency_ms}ms · anchor {result.anchor_entity || "—"}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Network Risk</div>
              <div data-testid="network-risk" style={{ color: riskColor, fontSize: 24, fontWeight: 800, marginTop: 4 }}>
                {result.network_risk}
              </div>
              <div style={{ color: "#64748b", fontSize: 10, marginTop: 4 }}>
                Priority: {result.investigation_priority}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Network Size</div>
              <div data-testid="network-size" style={{ color: "#60a5fa", fontSize: 36, fontWeight: 800 }}>
                {result.network_size}
              </div>
              <div style={{ color: "#64748b", fontSize: 10 }}>linked accounts</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>STR Required</div>
              <div data-testid="str-required" style={{ color: result.str_required ? "#fca5a5" : "#22c55e", fontSize: 16, fontWeight: 800, marginTop: 6 }}>
                {result.str_required ? "YES — 7 days" : "Not required"}
              </div>
              <div style={{ color: "#64748b", fontSize: 9, marginTop: 4 }}>
                Ring detected: {result.fraud_ring_detected ? "YES" : "no"}
              </div>
            </div>
          </div>

          {result.fraud_patterns?.length > 0 && (
            <section style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14, marginBottom: 12 }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <ShieldAlert size={11} style={{ color: "#dc2626" }} /> Fraud Patterns Detected ({result.fraud_patterns.length})
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {result.fraud_patterns.map((p, i) => (
                  <div key={i} data-testid={`pattern-${i}`} style={{
                    background: "rgba(220,38,38,0.08)", border: "1px solid #dc262655",
                    borderRadius: 8, padding: "10px 12px",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <span style={{ color: "#fca5a5", fontSize: 13, fontWeight: 800 }}>
                        {p.pattern.replace(/_/g, " ")}
                      </span>
                      <span style={{ marginLeft: "auto", color: "#fca5a5", fontSize: 10, fontWeight: 700 }}>
                        {p.confidence}% confidence
                      </span>
                      <span style={{ color: "#94a3b8", fontSize: 11, fontWeight: 700 }}>
                        {p.total_amount}
                      </span>
                    </div>
                    <div style={{ color: "#cbd5e1", fontSize: 11, marginBottom: 4 }}>{p.evidence}</div>
                    <div style={{ color: "#64748b", fontSize: 10 }}>
                      Accounts: {(p.accounts_involved || []).join(", ")}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {result.recommended_actions?.length > 0 && (
            <section style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14, marginBottom: 12 }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <FileWarning size={11} style={{ color: "#f59e0b" }} /> Recommended Actions
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                {result.recommended_actions.map((a, i) => {
                  const isImmediate = a.priority === "IMMEDIATE";
                  const c = isImmediate ? "#dc2626" : "#f59e0b";
                  return (
                    <div key={`${a.priority}-${a.action}`} data-testid={`action-${i}`} style={{
                      background: `${c}10`, border: `1px solid ${c}55`, borderRadius: 6,
                      padding: "8px 11px",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <AlertTriangle size={12} style={{ color: c }} />
                        <span style={{ color: c, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{a.priority}</span>
                        <span style={{ color: "#f1f5f9", fontSize: 12, fontWeight: 600 }}>{a.action}</span>
                      </div>
                      {a.accounts_affected?.length > 0 && (
                        <div style={{ color: "#94a3b8", fontSize: 10, marginTop: 4, marginLeft: 20 }}>
                          {a.accounts_affected.length} account(s): {a.accounts_affected.slice(0, 6).join(", ")}
                          {a.accounts_affected.length > 6 ? ` +${a.accounts_affected.length - 6} more` : ""}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {result.nodes?.length > 0 && (
            <section style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14, marginBottom: 12 }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <Sparkles size={11} style={{ color: "#60a5fa" }} /> Nodes ({result.nodes.length})
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 6 }}>
                {result.nodes.map((n) => {
                  const c = RISK[n.risk_level] || "#94a3b8";
                  return (
                    <div key={n.id} style={{
                      background: "#080c12", border: `1px solid ${c}55`, borderRadius: 6,
                      padding: "7px 10px",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: NODE_TYPE_COLOR[n.type] || "#94a3b8" }} />
                        <span style={{ color: "#f1f5f9", fontSize: 11, fontWeight: 700 }}>{n.name || n.id}</span>
                        <span style={{ marginLeft: "auto", color: c, fontSize: 9, fontWeight: 700 }}>{n.risk_level}</span>
                      </div>
                      <div style={{ color: "#64748b", fontSize: 9, marginTop: 3 }}>
                        {n.type}{n.fraud_signals?.length ? ` · ${n.fraud_signals.join(", ")}` : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
